'use client';

import { useEffect, useRef } from 'react';
import Link from 'next/link';
import { ArrowUpRight } from '@phosphor-icons/react';
import { OPEN_HOUR, CLOSE_HOUR } from '@/lib/business-hours';
import './delivery-journey.css';

export function DeliveryJourney() {
  const sectionRef = useRef<HTMLElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const courierRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const track = trackRef.current;
    const courier = courierRef.current;
    if (!section || !track || !courier) return;
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0;
    let visible = false;

    const draw = () => {
      frame = 0;
      const rect = section.getBoundingClientRect();
      const travel = Math.min(72, track.clientWidth * 0.12);
      const distance = Math.min(rect.height, window.innerHeight) * 0.85 + window.innerHeight * 0.15;
      const progress = preference.matches
        ? 0.5
        : Math.max(0, Math.min(1, (window.innerHeight * 0.82 - rect.top) / distance));
      section.style.setProperty('--journey-progress', String(progress));
      courier.style.transform = `translate3d(${((progress - 0.5) * travel).toFixed(1)}px,0,0)`;
    };
    const schedule = () => {
      if (visible && !frame) frame = window.requestAnimationFrame(draw);
    };
    const observer = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible) {
          window.addEventListener('scroll', schedule, { passive: true });
          schedule();
        } else {
          window.removeEventListener('scroll', schedule);
        }
      },
      { rootMargin: '100px 0px' }
    );
    const resize = new ResizeObserver(schedule);
    observer.observe(section);
    resize.observe(track);
    preference.addEventListener('change', schedule);
    window.addEventListener('resize', schedule, { passive: true });
    draw();
    return () => {
      observer.disconnect();
      resize.disconnect();
      preference.removeEventListener('change', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule);
      window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <section className="delivery-journey" ref={sectionRef} aria-labelledby="delivery-journey-title">
      <div className="delivery-journey-copy">
        <span className="delivery-journey-eyebrow">Азиаточка с доставкой</span>
        <h2 id="delivery-journey-title">
          Вы выбираете.
          <br />
          <span>Мы привозим.</span>
        </h2>
        <p>
          Домой, в офис или к друзьям.
          <br />
          Любимые роллы приедут к вам.
        </p>
        <Link href="/#menu">
          Выбрать роллы <ArrowUpRight size={23} />
        </Link>
        <span className="delivery-journey-hours">
          Каждый день · {OPEN_HOUR}:00–{CLOSE_HOUR}:00
        </span>
      </div>
      <div className="delivery-journey-scene" ref={trackRef} aria-hidden="true">
        <div className="delivery-journey-disc" />
        <div className="delivery-journey-courier" ref={courierRef}>
          <img
            src="/storefront/delivery-courier-v1.webp"
            width={1024}
            height={683}
            alt=""
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>
    </section>
  );
}
